import { useState } from "react";
import http from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ACCESSORIES } from "./AdminRepairCard";
import { Zap } from "lucide-react";

const inpCls = "mt-1 bg-slate-700 border-slate-600 text-white";

const EMPTY = {
  customer_name: "", phone: "", email: "", is_business: false,
  machine_type: "", machine_brand: "", machine_model: "", serial_number: "",
  problem_description: "", accessories: {}, accessories_notes: "",
  received_by: "Vadim", handover_assist: "",
};

export default function AdminQuickIntake({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => setForm(EMPTY);

  const save = async (openCard) => {
    if (!form.customer_name.trim() || !form.machine_brand.trim() || !form.problem_description.trim()) {
      toast.error("Заполните имя клиента, марку и описание проблемы"); return;
    }
    setSaving(true);
    try {
      const r = await http.post("/v1/admin/repair-orders", { ...form, status: "mottatt", priority: "normal" });
      toast.success(`Принято: ${r.data.order_number}`);
      onOpenChange(false); reset();
      onCreated && onCreated(r.data.id, openCard);
    } catch (err) { toast.error(err?.response?.data?.detail || "Ошибка сохранения"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="quick-intake">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><Zap className="h-5 w-5 text-amber-400" />Быстрый приём</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Быстрый приём машины (~1-2 мин). Диагностику и остальное можно добавить позже в карточке.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400">Имя / компания *</label><Input data-testid="qi-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">Телефон</label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">E-mail</label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">Тип клиента</label>
              <select value={form.is_business ? "1" : "0"} onChange={(e) => set("is_business", e.target.value === "1")} className={`${inpCls} w-full rounded px-2 py-2 text-sm border`}>
                <option value="0">Частный</option><option value="1">Бизнес</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400">Тип машины</label><Input value={form.machine_type} onChange={(e) => set("machine_type", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">Марка *</label><Input data-testid="qi-machine-brand" value={form.machine_brand} onChange={(e) => set("machine_brand", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">Модель</label><Input value={form.machine_model} onChange={(e) => set("machine_model", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">Серийный номер</label><Input value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} className={inpCls} /></div>
          </div>

          <div><label className="text-xs text-slate-400">Заявленная проблема *</label><Textarea data-testid="qi-problem" value={form.problem_description} onChange={(e) => set("problem_description", e.target.value)} className={inpCls} /></div>

          <div className="border border-slate-700 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-2">Прибыло с машиной</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ACCESSORIES.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={!!form.accessories[k]} onChange={(e) => set("accessories", { ...form.accessories, [k]: e.target.checked })} className="accent-blue-500" data-testid={`qi-acc-${k}`} />
                  {label}
                </label>
              ))}
            </div>
            <div className="mt-2"><Input placeholder="Другое / заметки" value={form.accessories_notes} onChange={(e) => set("accessories_notes", e.target.value)} className={inpCls} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400">Кто принял (ответственный)</label><Input data-testid="qi-received-by" value={form.received_by} onChange={(e) => set("received_by", e.target.value)} className={inpCls} /></div>
            <div><label className="text-xs text-slate-400">Помогал при приёме/выдаче (необязательно)</label><Input value={form.handover_assist} onChange={(e) => set("handover_assist", e.target.value)} className={inpCls} /></div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700" disabled={saving} onClick={() => save(false)} data-testid="qi-save-close">Сохранить и закрыть</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving} onClick={() => save(true)} data-testid="qi-save-open">Сохранить и открыть карточку</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
